<template>
  <div>
    <div class="ch-head">
      <div>
        <h1>培训中心</h1>
        <p>看完课件才算学完。视频要播完，快进不算；文档要在页面里阅读。学完一个阶段才能考该阶段。不及格可以补考，不影响账号和邀请码。证书由总部上传。</p>
      </div>
    </div>
    <div class="ch-metrics">
      <div class="ch-metric"><div class="k">必修完成度</div><div class="v">{{ percent }}%</div><div class="s">已完成 {{ finished }} / {{ lessons }} 课时</div></div>
      <div class="ch-metric"><div class="k">课程数</div><div class="v">{{ list.length }}</div><div class="s">停用课程不会出现在这里</div></div>
      <div class="ch-metric"><div class="k">最近考试</div><div class="v" style="font-size:18px">{{ examText }}</div><div class="s">按阶段抽题，答对达标即通过</div></div>
      <div class="ch-metric"><div class="k">结业证书</div><div class="v" style="font-size:18px">{{ certificateUrl ? '已上传' : '未上传' }}</div><div class="s">总部上传后在这里查看</div></div>
    </div>
    <div class="ch-tabs">
      <button type="button" :class="{ on: tab === 'courses' }" @click="tab = 'courses'">课程</button>
      <button type="button" :class="{ on: tab === 'exam' }" @click="tab = 'exam'">考试</button>
      <button type="button" :class="{ on: tab === 'cert' }" @click="tab = 'cert'">证书</button>
    </div>
    <div v-if="tab === 'courses'" class="ch-panel">
      <table class="ch-table">
        <thead><tr><th>课程</th><th>阶段</th><th>进度</th><th>状态</th><th>操作</th></tr></thead>
        <tbody>
          <tr v-for="row in list" :key="row.id">
            <td>{{ row.title }}</td>
            <td>{{ stageName(row.stage) }}</td>
            <td>{{ row.finishedLessons }} / {{ row.lessons }}</td>
            <td><span class="ch-tag" :class="done(row) ? 'ok' : 'warn'">{{ done(row) ? '已看完' : '未看完' }}</span></td>
            <td>
              <button v-if="row.fileUrl" class="ch-link" type="button" @click="openFile(row)">{{ done(row) ? '再看一遍' : '看课件' }}</button>
              <span v-else>待总部上传课件</span>
            </td>
          </tr>
          <tr v-if="!list.length"><td colspan="5" class="ch-empty">还没有课程</td></tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="tab === 'exam'" class="ch-panel">
      <div class="stage-row">
        <button v-for="item in examStages" :key="item.id" type="button" class="ch-btn" :class="{ gold: examStage === item.id }" @click="pickStage(item.id)">{{ item.label }}</button>
      </div>
      <p v-if="!stageReady" class="ch-note">请先学完「{{ stageName(examStage) }}」里已上传课件的课程，再参加考试。</p>
      <p v-else-if="!stageQuestions.length" class="ch-empty">这个阶段还没有启用的题目。</p>
      <div v-for="item in shownQuestions" :key="item.id" class="question">
        <p>{{ item.stem }} <em>{{ typeName(item.questionType) }}</em></p>
        <label v-for="key in optionKeys(item)" :key="key">
          <input v-if="item.questionType === 'MULTI'" type="checkbox" :checked="selected(item, key)" @change="toggle(item, key)" />
          <input v-else v-model="answers[item.id]" type="radio" :value="key" />
          {{ key }}. {{ item['option' + key] }}
        </label>
      </div>
      <button v-if="shownQuestions.length" class="ch-btn gold exam-submit" type="button" :disabled="submitting" @click="exam">交卷</button>
      <h2 v-if="stageAttempts.length">考试记录</h2>
      <table v-if="stageAttempts.length" class="ch-table">
        <thead><tr><th>时间</th><th>阶段</th><th>得分</th><th>结果</th></tr></thead>
        <tbody>
          <tr v-for="row in stageAttempts" :key="row.id">
            <td>{{ row.createdAt }}</td>
            <td>{{ stageName(row.stage) }}</td>
            <td>{{ row.score }}</td>
            <td>{{ Number(row.passed) === 1 ? '通过' : '未通过，可补考' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="ch-panel">
      <h2>结业证书</h2>
      <a v-if="certificateUrl" :href="certificateUrl" target="_blank" rel="noopener">查看证书</a>
      <p v-else class="ch-empty">总部还没有上传证书。</p>
    </div>
    <div v-if="viewer" class="viewer" @click.self="closeViewer">
      <div class="viewer-card">
        <video v-if="isVideo(viewer.url)" :src="viewer.url" controls @timeupdate="onTime" @ended="onEnded" />
        <iframe v-else :src="viewer.url" title="课件" />
        <p class="watch-note">{{ watchNote }}</p>
        <button class="ch-btn" type="button" @click="closeViewer">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getChannelCourses, getChannelQuestions, submitChannelExam, saveChannelCourseProgress } from '@/api/admin'

const STAGE_LABELS = {
  '一阶': '第一阶产品培训',
  '二阶': '第二阶销售培训',
  '三阶': '第三阶解决方案培训',
  '结业': '结业'
}
const FALLBACK_STAGES = ['一阶', '二阶', '三阶', '结业']

export default {
  name: 'PartnerTraining',
  data() {
    return { tab: 'courses', list: [], lessons: 0, finished: 0, questions: [], attempts: [], answers: {}, certificateUrl: '', examStage: '一阶', stages: FALLBACK_STAGES.slice(), configs: [], submitting: false, viewer: null, played: 0, lastTime: 0, dwell: 0, dwellTimer: null, marking: false }
  },
  computed: {
    percent() { return this.lessons ? Math.round(this.finished * 100 / this.lessons) : 0 },
    examStages() { return this.stages.map(id => ({ id, label: this.stageName(id) })) },
    stageReady() {
      const rows = this.list.filter(row => row.stage === this.examStage && row.fileUrl)
      if (!rows.length) return true
      const lessons = rows.reduce((sum, row) => sum + Number(row.lessons || 0), 0)
      const finished = rows.reduce((sum, row) => sum + Number(row.finishedLessons || 0), 0)
      return lessons > 0 && finished >= lessons
    },
    stageQuestions() {
      return this.questions.filter(item => this.examStage === '结业' ? !item.stage || item.stage === '结业' : item.stage === this.examStage)
    },
    shownQuestions() {
      const config = this.configs.find(item => item.stage === this.examStage)
      const draw = config ? Number(config.drawCount) : this.stageQuestions.length
      return draw > 0 ? this.stageQuestions.slice(0, draw) : this.stageQuestions
    },
    stageAttempts() {
      return this.attempts.filter(row => (row.stage || '结业') === this.examStage)
    },
    examText() {
      if (!this.attempts.length) return '未考试'
      const last = this.attempts[0]
      return (Number(last.passed) === 1 ? '已通过 ' : '未通过 ') + last.score + ' 分'
    }
  },
  created() { this.load() },
  beforeDestroy() { this.clearDwell() },
  methods: {
    stageName(stage) {
      return STAGE_LABELS[stage] || stage || '—'
    },
    watchNote() {
      if (!this.viewer) return ''
      if (this.viewer.done) return '这门课已经看完。'
      if (this.isVideo(this.viewer.url)) return '请按顺序看完，快进不计入进度。'
      const left = Math.max(0, 60 - this.dwell)
      return left > 0 ? '请阅读课件，' + left + ' 秒后记入进度。' : '已记入进度。'
    },
    typeName(type) { return { MULTI: '多选', JUDGE: '判断' }[type] || '单选' },
    optionKeys(item) { return item.questionType === 'JUDGE' ? ['A', 'B'] : ['A', 'B', 'C', 'D'].filter(key => item['option' + key] && item['option' + key] !== '—') },
    done(row) { return Number(row.finishedLessons) >= Number(row.lessons) },
    isVideo(url) { return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || '') },
    openFile(row) {
      this.clearDwell()
      this.viewer = { url: row.fileUrl, courseId: row.id, done: this.done(row) }
      this.played = 0
      this.lastTime = 0
      this.dwell = 0
      if (!this.viewer.done && !this.isVideo(row.fileUrl)) {
        this.dwellTimer = setInterval(() => {
          this.dwell += 1
          if (this.dwell >= 60) this.markWatched()
        }, 1000)
      }
    },
    closeViewer() {
      this.clearDwell()
      this.viewer = null
    },
    clearDwell() {
      if (this.dwellTimer) {
        clearInterval(this.dwellTimer)
        this.dwellTimer = null
      }
    },
    onTime(event) {
      if (!this.viewer || this.viewer.done) return
      const video = event.target
      const delta = video.currentTime - this.lastTime
      this.lastTime = video.currentTime
      if (delta > 0 && delta < 2) this.played += delta
      if (video.duration && this.played >= video.duration * 0.9) this.markWatched()
    },
    onEnded(event) {
      const video = event.target
      if (video.duration && this.played < video.duration * 0.9) {
        this.$message.warning('快进不计入进度，请看完课件')
        return
      }
      this.markWatched()
    },
    async markWatched() {
      if (!this.viewer || this.viewer.done || this.marking) return
      this.marking = true
      this.clearDwell()
      try {
        await saveChannelCourseProgress({ courseId: this.viewer.courseId })
        this.viewer.done = true
        this.$message.success('已看完，进度已记下')
        this.load()
      } finally {
        this.marking = false
      }
    },
    selected(item, key) { return String(this.answers[item.id] || '').split(',').indexOf(key) >= 0 },
    toggle(item, key) {
      const set = new Set(String(this.answers[item.id] || '').split(',').filter(Boolean))
      if (set.has(key)) set.delete(key)
      else set.add(key)
      this.$set(this.answers, item.id, Array.from(set).sort().join(','))
    },
    pickStage(stage) { this.examStage = stage },
    async load() {
      const result = await getChannelCourses()
      this.list = result.list || []
      this.stages = (result.stages && result.stages.length) ? result.stages : FALLBACK_STAGES.slice()
      if (this.stages.indexOf(this.examStage) < 0) this.examStage = this.stages[0]
      this.lessons = result.lessons || 0
      this.finished = result.finished || 0
      this.certificateUrl = result.certificateUrl || ''
      const exam = await getChannelQuestions()
      this.questions = exam.list || []
      this.attempts = exam.attempts || []
      this.configs = exam.configs || []
    },
    async exam() {
      if (!this.stageReady) {
        this.$message.warning('请先学完「' + this.stageName(this.examStage) + '」里已上传课件的课程')
        return
      }
      const answers = {}
      this.shownQuestions.forEach(item => { answers[item.id] = this.answers[item.id] || '' })
      this.submitting = true
      try {
        const result = await submitChannelExam({ stage: this.examStage, answers })
        this.attempts = result.attempts || []
        this.$message.success(this.examText)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.stage-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.exam-submit { margin-top: 8px; }
.question { margin: 0 0 14px; padding-bottom: 10px; border-bottom: 1px solid #eef1f6; }
.question p { margin: 0 0 6px; color: #1a2468; }
.question em { margin-left: 8px; color: #8b93a7; font-style: normal; font-size: 12px; }
.question label { display: block; margin: 4px 0; color: #344054; }
.ch-link + .ch-link, a.ch-link + .ch-link { margin-left: 16px; }
.viewer { position: fixed; inset: 0; z-index: 3000; background: rgba(16, 22, 56, 0.55); display: flex; align-items: center; justify-content: center; padding: 24px; }
.viewer-card { width: min(960px, 100%); background: #fff; border-radius: 12px; padding: 16px; }
.viewer-card video, .viewer-card iframe { width: 100%; height: min(70vh, 540px); background: #111; border: 0; }
.viewer-card .ch-btn { margin-top: 12px; }
.watch-note { margin: 10px 0 0; color: #5c6780; font-size: 13px; }